import { toastTopCenterOptions, toastDefaultOptions } from '@/config/vue-toast'
import store from '@/store'
import { authGetters, logoutLocalRoutine } from '@/store/modules/auth/routines'
import { notificationRoutine } from '@/store/modules/notification/routines'
import { defaultGroupId } from '@/utils/constants'
import useWhitelabel from '@/composables/useWhitelabel'
import * as Sentry from '@sentry/vue'
import { addRequest, pendingRequests } from '@swoop-ltd/api-request-manager'
import axios from 'axios'
import axiosRetry from 'axios-retry'
import qs from 'qs'
import Vue from 'vue'

function resolveGroupId() {
  const storeGroupId = store.getters[authGetters.GROUP_ID]
  if (store.getters[authGetters.IS_AUTHENTICATED] || store.getters[authGetters.IS_INVITED_USER]) {
    return storeGroupId
  }
  const { config: whitelabelGlobalConfig } = useWhitelabel('global')
  return whitelabelGlobalConfig.value?.groupId ?? storeGroupId
}

export function createAxiosInstance(baseURL) {
  const axiosInstance = axios.create({
    baseURL,
    paramsSerializer: params => qs.stringify(params, { encode: false })
  })

  axiosRetry(axiosInstance, {
    retryDelay: axiosRetry.exponentialDelay,
    shouldResetTimeout: true
  })
  axiosInstance.interceptors.response.use(
    response => {
      const { config } = response
      if (config && config.data && config.data.requestId) {
        delete pendingRequests[config.data.requestId]
        delete config.data.requestId
      }
      return response
    },
    error => {
      if (Vue.prototype.$apm && error instanceof Error) {
        Vue.prototype.$apm.captureError(error)
      } else if (Vue.prototype.$apm && error.response) {
        Vue.prototype.$apm.captureError({
          message: error.message,
          response: error.response
        })
      }
      if (error.response) {
        if (error.response.status >= 500) {
          Sentry.captureException(new Error(`Server error ${error.response.status}`), {
            extra: {
              url: error.config.url,
              method: error.config.method,
              status: error.response.status,
              message: error.response.data?.message || 'No message'
            },
            tags: {
              statusCode: error.response.status
            }
          })
        }

        if (error.response.status === 401 && store.getters[authGetters.GROUP_ID] !== defaultGroupId) {
          store.dispatch(logoutLocalRoutine.TRIGGER)
          store.dispatch(notificationRoutine.TRIGGER, {
            group: 'auth',
            type: 'error',
            title: 'Logout',
            text: 'GroupId mismatch, please login through the correct whitelabel.'
          })
          return Promise.reject(error)
        }

        const signinRoutes = ['login', 'xero', 'xerosignup']
        const selfHandled400Routes = ['get-started', 'api-onboarding']
        const selfHandledErrorRoutes = ['api-onboarding']
        const currentRoute = window.location.pathname.split('/')[1]

        switch (error.response.status) {
          case 400:
            if (signinRoutes.includes(currentRoute)) {
              store.dispatch(notificationRoutine.TRIGGER, {
                title: 'API Error',
                options: { ...toastTopCenterOptions, className: 'toasted-error' },
                text: 'Your Email or Password is incorrect'
              })
            } else if (error.config.method === 'delete' && error.config.url.includes('/opportunities/')) {
              return Promise.resolve()
            } else if (!selfHandled400Routes.includes(currentRoute)) {
              store.dispatch(notificationRoutine.TRIGGER, {
                title: 'API Error',
                options: { ...toastTopCenterOptions, className: 'toasted-error' },
                text: error.response.data?.message ? error.response.data.message : 'There was an error processing your request'
              })
            }

            break
          case 401:
            if (!signinRoutes.includes(currentRoute)) {
              store.dispatch(logoutLocalRoutine.TRIGGER)
            }

            break
          case 403:
            if (!signinRoutes.includes(currentRoute))
              store.dispatch(notificationRoutine.TRIGGER, {
                title: 'API Error',
                options: { ...toastTopCenterOptions, className: 'toasted-warning' },
                text: "You don't have permission to access this resource. Please contact your administrator if you need access."
              })

            break
          case 404:
            store.dispatch(notificationRoutine.TRIGGER, {
              title: 'API Error',
              options: { ...toastDefaultOptions, className: 'toasted-error' },
              text: 'There was an error processing your request'
            })

            break
          case 408:
            store.dispatch(notificationRoutine.TRIGGER, {
              title: 'API Error',
              options: { ...toastTopCenterOptions, className: 'toasted-error' },
              text: 'We encountered an error: The server took to long to respond'
            })

            break
          case 422:
            // A `code` means the caller handles this 422 itself (e.g. creditScore's "already registered" state).
            if (!error.response.data?.code) {
              store.dispatch(notificationRoutine.TRIGGER, {
                title: 'API Error',
                options: { ...toastTopCenterOptions, className: 'toasted-error' },
                text: error.response.data?.message || 'There was an error processing your request'
              })
            }

            break
          case 429:
            store.dispatch(notificationRoutine.TRIGGER, {
              title: 'API Error',
              options: { ...toastTopCenterOptions, className: 'toasted-error' },
              text: error.response.data?.message
            })

            break
          case 500:
            if (!selfHandledErrorRoutes.includes(currentRoute)) {
              store.dispatch(notificationRoutine.TRIGGER, {
                title: 'API Error',
                options: { ...toastTopCenterOptions, className: 'toasted-error' },
                text: 'There was an internal server error'
              })
            }

            break
          case 501:
            store.dispatch(notificationRoutine.TRIGGER, {
              title: 'API Error',
              options: { ...toastTopCenterOptions, className: 'toasted-error' },
              text: 'The requested method is not yet supported by our server'
            })

            break
          default:
            if (!import.meta.env.PROD) {
              store.dispatch(notificationRoutine.TRIGGER, {
                type: 'error',
                title: 'API Error',
                text: `Status code: ${error.response.status}\n${(error.response.data && error.response.data.message) || 'Unknown.\nNo Response Data.'}`
              })
            }
        }
      } else if (error.request) {
        Sentry.captureException(new Error('No response received from server'))
        store.dispatch(notificationRoutine.TRIGGER, {
          title: 'Network Error',
          options: { ...toastTopCenterOptions, className: 'toasted-error' },
          text: 'No response received from server. Please check your connection or try again later.'
        })
      } else {
        Sentry.captureException(new Error(error.message))
        store.dispatch(notificationRoutine.TRIGGER, {
          title: 'Request Error',
          options: { ...toastTopCenterOptions, className: 'toasted-error' },
          text: error.message || 'Something went wrong, please try again later.'
        })
        console.log('Unknown Error', error)
      }
      return Promise.reject(error)
    }
  )

  axiosInstance.interceptors.request.use(config => {
    const token = store.getters[authGetters.FULL_TOKEN]
    if (token) {
      config.headers.Authorization = token
    }

    // set the default headers for each request.
    const groupId = resolveGroupId()
    config.headers['X-Group-Id'] = groupId
    config.headers['X-Group-Ids'] = groupId
    config.headers['Content-Type'] = ['application/json']
    config.headers['X-Application-Type'] = 'marketplace'

    if (config.data?.requestId) {
      const source = axios.CancelToken.source()
      config.cancelToken = source.token
      addRequest(config.data.requestId, source.cancel)
    }
    return config
  })

  return axiosInstance
}

export const swoopAPI = createAxiosInstance(import.meta.env.VITE_BASE_API_URL)

export const swoopCompaniesApi = createAxiosInstance(import.meta.env.VITE_BASE_COMPANIES_API_URL)

export const swoopAggregatorApi = createAxiosInstance(import.meta.env.VITE_BASE_AGGREGATOR_API_URL)

export const swoopApiUrl = createAxiosInstance(import.meta.env.VITE_API_URL)

export const swoopIllionApiUrl = createAxiosInstance(import.meta.env.VITE_API_BASE_ILLION_URL)

export const accountingApi = createAxiosInstance(import.meta.env.VITE_ACCOUNTING_API_URL)

export const formFieldsMetaData = createAxiosInstance(import.meta.env.VITE_FORM_FIELDS_METADATA_URL)

export const recommendationApi = createAxiosInstance(import.meta.env.VITE_RECOMMENDATION_API_URL)

export const brokerApi = createAxiosInstance(import.meta.env.VITE_BROKER_API_URL)

export const IAMAPI = createAxiosInstance(import.meta.env.VITE_IAM_API_URL)

export const publicApi = createAxiosInstance(import.meta.env.VITE_PUBLIC_API_URL)

export const dynamicFormsApi = createAxiosInstance(import.meta.env.VITE_DYNAMIC_FORMS_API_URL)

export const azureAdApi = axios.create({
  baseURL: import.meta.env.VITE_AZURE_AD_API_URL
})

export const applyRequestHeadersToAllInstances = newHeaders => {
  const instances = [swoopAPI, swoopCompaniesApi, swoopAggregatorApi, swoopApiUrl, swoopIllionApiUrl, accountingApi, recommendationApi, brokerApi, dynamicFormsApi, publicApi]

  instances.forEach(instance => {
    instance.interceptors.request.use(requestConfig => {
      return {
        ...requestConfig,
        headers: {
          ...requestConfig.headers,
          ...newHeaders
        }
      }
    })
  })
}

export default axios
